import type { Observable, Subscription } from "rxjs";
import { errorLoggingDecorator } from "../logger/errorLoggingDecorator";
import { getLogger } from "../logger/logger";
import { LensPerformanceMeasurement } from "./LensPerformanceMeasurement";
import type { FrameEvent } from "./frameEvents";

const logger = getLogger("RenderingMetrics");

/**
 * Use to measure lens rendering performance.
 *
 * The {@link LensPerformanceMetrics.beginMeasurement} method is used to start measuring rendering performance. After
 * a measurement has begun, performance metrics can be read using {@link LensPerformanceMeasurement}.
 *
 * @example
 * ```ts
 * const measurement = cameraKitSession.metrics.beginMeasurement()
 * // some time later
 * console.log(measurement.measure())
 * ```
 *
 * @category Rendering
 * @category Metrics
 */
export class LensPerformanceMetrics {
    private readonly measurementInstances: Set<LensPerformanceMeasurement>;
    private readonly subscription: Subscription;

    /** @internal */
    constructor(frameEvents: Observable<FrameEvent>) {
        this.measurementInstances = new Set();
        this.subscription = frameEvents.subscribe({
            next: (frameEvent) => {
                try {
                    for (const measurement of this.measurementInstances.values()) {
                        measurement.update(frameEvent.processingTimeMs);
                    }
                } catch (error) {
                    logger.error(error);
                }
            },
            error: (error) => {
                logger.error(error);
            },
        });
    }

    /**
     * Begin a measurement window, during which time rendering metrics will be gathered.
     *
     * @returns A {@link LensPerformanceMeasurement} representing an ongoing measurement of aggregated rendering
     * metrics, from which specific metrics can be obtained.
     */
    @errorLoggingDecorator(logger)
    beginMeasurement(): LensPerformanceMeasurement {
        return new LensPerformanceMeasurement(this.measurementInstances);
    }

    destroy() {
        this.subscription.unsubscribe();
    }
}
